import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Building2, Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { DeleteConfirmationModal } from '../common/DeleteConfirmationModal';
import { BankForm } from './BankForm';
import { BankFeeForm } from './BankFeeForm';
import { Link } from 'react-router-dom';
import { Bank } from '../../types';

export function BankManagement() {
  const [banks, setBanks] = useState<Bank[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewBankForm, setShowNewBankForm] = useState(false);
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);
  const [bankToDelete, setBankToDelete] = useState<Bank | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchBanks();
  }, []);

  async function fetchBanks() {
    try {
      const { data, error } = await supabase
        .from('banks')
        .select('*')
        .order('name');
      
      if (error) throw error;
      setBanks(data || []);
    } catch (error) {
      console.error('Error fetching banks:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleDelete = async () => {
    if (!bankToDelete) return;
    
    setDeleting(true);

    try {
      const { error } = await supabase
        .rpc('soft_delete_bank', {
          bank_id_param: bankToDelete.id
        });

      if (error) throw error;
      
      setBanks(banks.filter(b => b.id !== bankToDelete.id));
      setBankToDelete(null);
    } catch (err) {
      console.error('Error deleting bank:', err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="page-header">
        <div>
          <h1 className="page-title">Providers</h1>
          <p className="page-subtitle">Manage providers and their transaction fee structures</p>
        </div>
        <button onClick={() => setShowNewBankForm(true)} className="btn-primary">
          <Plus className="w-4 h-4" />
          Add Provider
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : banks.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-7 h-7 text-slate-400" />
          </div>
          <h3 className="text-[15px] font-semibold text-slate-900">No providers</h3>
          <p className="mt-1 text-[14px] text-slate-500">Get started by adding a new provider.</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="px-5 py-3 text-left text-[12px] font-medium text-slate-400 uppercase tracking-wider">Name</th>
                  <th className="px-5 py-3 text-left text-[12px] font-medium text-slate-400 uppercase tracking-wider">Alias</th>
                  <th className="px-5 py-3 text-right text-[12px] font-medium text-slate-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {banks.map((bank) => (
                  <tr key={bank.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-4">
                      <Link
                        to={`/banks/${bank.id}`}
                        className="text-[14px] font-medium text-slate-900 hover:text-violet-600 transition-colors"
                      >
                        {bank.name}
                      </Link>
                    </td>
                    <td className="px-5 py-4 text-[14px] text-slate-500">{bank.alias}</td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        <button
                          onClick={() => setSelectedBank(bank)}
                          className="btn-ghost"
                          title="Edit fees"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setBankToDelete(bank)}
                          disabled={deleting}
                          className="btn-ghost hover:!text-red-500 hover:!bg-red-50"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedBank && (
        <BankFeeForm
          bank={selectedBank}
          onClose={() => setSelectedBank(null)}
        />
      )}
      {showNewBankForm && (
        <BankForm
          onClose={() => setShowNewBankForm(false)}
          onSuccess={() => {
            fetchBanks();
          }}
        />
      )}
      <DeleteConfirmationModal
        title="Delete Provider"
        message={`Are you sure you want to delete "${bankToDelete?.name}"? This provider will no longer be available for new quotations, but existing quotations will be preserved.`}
        isOpen={!!bankToDelete}
        isDeleting={deleting}
        onConfirm={handleDelete}
        onCancel={() => setBankToDelete(null)}
      />
    </div>
  );
}
